import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDownIcon, Link2, UserPen, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganizationMember } from '@/common/organization';
import { Button } from '@/components/ui/button';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getOrganizationMembersOptions } from '@/queries/organizations/list-organization-members';

type ResponsibleUserPickerProps = {
	organizationId?: string;
	/** The linked user, or null while the specialist is typed by hand. */
	linkedUser: OrganizationMember | null;
	disabled?: boolean;
	onPick: (member: OrganizationMember) => void;
	/** "Not in the list": typed by hand, called once the picker has closed so focus can move. */
	onManual: () => void;
	onUnlink: () => void;
};

/**
 * Picks the asset's responsible specialist from the organisation's users. A picked user is
 * linked and the form locks name and email to them; anyone else is typed by hand.
 */
export function ResponsibleUserPicker({
	organizationId,
	linkedUser,
	disabled = false,
	onPick,
	onManual,
	onUnlink,
}: ResponsibleUserPickerProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	// Radix returns focus to the trigger on close; manual entry wants it in the Name field instead.
	const manualChosen = useRef(false);
	const {
		data: members = [],
		isLoading,
		isError,
	} = useQuery({
		...getOrganizationMembersOptions(organizationId),
		// The endpoint needs ASSETS_CREATE/UPDATE; for anyone who cannot edit it would only answer 403
		enabled: !disabled && !!organizationId,
	});

	let unavailableReason: string | null = null;
	if (disabled) unavailableReason = null;
	else if (!organizationId) unavailableReason = t('assets.form.specialist.noOrganization');
	else if (isError) unavailableReason = t('assets.form.specialist.loadFailed');
	else if (!isLoading && members.length === 0) unavailableReason = t('assets.form.specialist.noMembers');

	return (
		<div className="space-y-1.5">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						type="button"
						variant="outline"
						disabled={disabled || isLoading || !!unavailableReason}
						className="w-full justify-between bg-white font-normal"
					>
						{isLoading ? t('assets.form.specialist.loading') : t('assets.form.specialist.select')}
						<ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent
					align="start"
					className="p-0 w-(--radix-popover-trigger-width)"
					onCloseAutoFocus={(event) => {
						if (!manualChosen.current) return;
						manualChosen.current = false;
						event.preventDefault();
						onManual();
					}}
				>
					<Command>
						<CommandInput placeholder={t('assets.form.specialist.searchPlaceholder')} />
						<CommandList>
							<CommandEmpty>{t('assets.form.specialist.noMatch')}</CommandEmpty>
							<CommandGroup>
								{members.map((member) => (
									<CommandItem
										key={member.id}
										value={`${member.name} <${member.email}>`}
										onSelect={() => {
											onPick(member);
											setOpen(false);
										}}
									>
										<span className="flex min-w-0 flex-col">
											<span className="truncate">{member.name}</span>
											<span className="truncate text-xs text-muted-foreground">{member.email}</span>
										</span>
									</CommandItem>
								))}
							</CommandGroup>
							<CommandSeparator alwaysRender />
							<CommandGroup forceMount>
								<CommandItem
									forceMount
									value="manual-entry"
									onSelect={() => {
										manualChosen.current = true;
										setOpen(false);
									}}
								>
									<UserPen className="size-4" />
									{t('assets.form.specialist.enterManually')}
								</CommandItem>
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			{linkedUser ? (
				<div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
					<span className="flex min-w-0 items-center gap-1.5">
						<Link2 className="size-3.5 shrink-0" />
						<span className="truncate">{t('assets.form.specialist.linkedTo', { name: linkedUser.name })}</span>
					</span>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-6 px-2 text-xs"
						onClick={onUnlink}
						disabled={disabled}
					>
						<X className="size-3" />
						{t('assets.form.specialist.unlink')}
					</Button>
				</div>
			) : (
				unavailableReason && <p className="text-xs text-muted-foreground">{unavailableReason}</p>
			)}
		</div>
	);
}
